export type InventoryItem = {
    id: string;
    itemName: string;
    category: string;
    sku: string;
    availability: "In Stock" | "Low Stock" | "Critical" | "Out of Stock";
    quantity: number;
    reorderLevel: number;
    manufacturer?: string;
    storageCondition?: string;
    registrationNumber?: string;
    scientificName?: string;
    activeIngredient?: string;
    dosageForm?: string;
    administration?: string;
    pharmaClass?: string;
    shelfRack?: string;
};

export type InventoryActivity = {
    date: string;
    type: "Stock Received" | "Stock Requisitioned" | "Transfer Completed" | "Stock Return" | "Created Order" | "Inventory Update";
    amount: number;
    description: string;
};

export type InventoryItemDetail = InventoryItem & {
    recentActivity: InventoryActivity[];
    clinicalNotes?: string;
};

// Recent moving items (shown in overview)
export const RECENT_MOVEMENT_ITEMS: InventoryItem[] = [
    {
        id: "550e8400-e29b-41d4-a716-446655440001",
        itemName: "Ibuprofen 200mg",
        category: "Pain Relief",
        sku: "IBU-002",
        availability: "In Stock",
        quantity: 120,
        reorderLevel: 40,
        shelfRack: "Shelf Rack -1"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440002",
        itemName: "Metformin 500mg",
        category: "DiabetesCare",
        sku: "MET-005",
        availability: "Critical",
        quantity: 130,
        reorderLevel: 200,
        shelfRack: "Shelf Rack -2"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440003",
        itemName: "Lisinopril 10mg",
        category: "HeartHealth",
        sku: "LIS-007",
        availability: "In Stock",
        quantity: 140,
        reorderLevel: 30,
        shelfRack: "Shelf Rack -1"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440004",
        itemName: "Simvastatin 20mg",
        category: "CholesterolCo",
        sku: "SIM-006",
        availability: "In Stock",
        quantity: 190,
        reorderLevel: 10,
        shelfRack: "Shelf Rack -3"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440005",
        itemName: "Levothyroxine 50mcg",
        category: "ThyroidWell",
        sku: "LEV-007",
        availability: "Low Stock",
        quantity: 100,
        reorderLevel: 150,
        shelfRack: "Shelf Rack -2"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440006",
        itemName: "Amlodipine 5mg",
        category: "BloodPressurePlus",
        sku: "AML-003",
        availability: "In Stock",
        quantity: 180,
        reorderLevel: 20,
        shelfRack: "Shelf Rack -1"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440007",
        itemName: "Omeprazole 20mg",
        category: "DigestiveHealth",
        sku: "OME-009",
        availability: "In Stock",
        quantity: 900,
        reorderLevel: 0,
        shelfRack: "Shelf Rack -4"
    },
    {
        id: "550e8400-e29b-41d4-a716-446655440008",
        itemName: "Hydrochlorothiazide 25mg",
        category: "FluidBalance",
        sku: "HYD-009",
        availability: "Out of Stock",
        quantity: 0,
        reorderLevel: 200,
        shelfRack: "Shelf Rack -2"
    }
];

// Complete inventory list (for full inventory view)
export const COMPLETE_INVENTORY: InventoryItem[] = [
    ...RECENT_MOVEMENT_ITEMS,
    {
        id: "9",
        itemName: "Atorvastatin 40mg",
        category: "CholesterolCo",
        sku: "ATO-001",
        availability: "In Stock",
        quantity: 250,
        reorderLevel: 50
    },
    {
        id: "10",
        itemName: "Losartan 50mg",
        category: "HeartHealth",
        sku: "LOS-004",
        availability: "In Stock",
        quantity: 175,
        reorderLevel: 40
    },
    {
        id: "11",
        itemName: "Gabapentin 300mg",
        category: "NerveCare",
        sku: "GAB-012",
        availability: "Low Stock",
        quantity: 85,
        reorderLevel: 100
    },
    {
        id: "12",
        itemName: "Sertraline 100mg",
        category: "MentalWellness",
        sku: "SER-015",
        availability: "In Stock",
        quantity: 160,
        reorderLevel: 30
    },
    {
        id: "13",
        itemName: "Pantoprazole 40mg",
        category: "DigestiveHealth",
        sku: "PAN-018",
        availability: "In Stock",
        quantity: 220,
        reorderLevel: 45
    },
    {
        id: "14",
        itemName: "Albuterol Inhaler",
        category: "Respiratory",
        sku: "ALB-021",
        availability: "Critical",
        quantity: 25,
        reorderLevel: 50
    },
    {
        id: "15",
        itemName: "Furosemide 40mg",
        category: "FluidBalance",
        sku: "FUR-024",
        availability: "In Stock",
        quantity: 195,
        reorderLevel: 35
    },
    {
        id: "16",
        itemName: "Clopidogrel 75mg",
        category: "HeartHealth",
        sku: "CLO-027",
        availability: "In Stock",
        quantity: 140,
        reorderLevel: 40
    },
    {
        id: "17",
        itemName: "Tramadol 50mg",
        category: "Pain Relief",
        sku: "TRA-030",
        availability: "Low Stock",
        quantity: 60,
        reorderLevel: 80
    },
    {
        id: "18",
        itemName: "Prednisone 20mg",
        category: "Inflammation",
        sku: "PRE-033",
        availability: "In Stock",
        quantity: 180,
        reorderLevel: 50
    },
    {
        id: "19",
        itemName: "Warfarin 5mg",
        category: "BloodThinners",
        sku: "WAR-036",
        availability: "In Stock",
        quantity: 110,
        reorderLevel: 30
    },
    {
        id: "20",
        itemName: "Alprazolam 1mg",
        category: "MentalWellness",
        sku: "ALP-039",
        availability: "Critical",
        quantity: 45,
        reorderLevel: 60
    },
    {
        id: "21",
        itemName: "Carvedilol 25mg",
        category: "HeartHealth",
        sku: "CAR-042",
        availability: "In Stock",
        quantity: 150,
        reorderLevel: 40
    },
    {
        id: "22",
        itemName: "Montelukast 10mg",
        category: "Respiratory",
        sku: "MON-045",
        availability: "In Stock",
        quantity: 200,
        reorderLevel: 50
    },
    {
        id: "23",
        itemName: "Trazodone 100mg",
        category: "MentalWellness",
        sku: "TRZ-048",
        availability: "Low Stock",
        quantity: 70,
        reorderLevel: 90
    },
    {
        id: "24",
        itemName: "Meloxicam 15mg",
        category: "Pain Relief",
        sku: "MEL-051",
        availability: "In Stock",
        quantity: 135,
        reorderLevel: 35
    },
    {
        id: "25",
        itemName: "Ranitidine 150mg",
        category: "DigestiveHealth",
        sku: "RAN-054",
        availability: "Out of Stock",
        quantity: 0,
        reorderLevel: 100
    },
    {
        id: "26",
        itemName: "Doxycycline 100mg",
        category: "Antibiotics",
        sku: "DOX-057",
        availability: "In Stock",
        quantity: 280,
        reorderLevel: 60
    },
    {
        id: "27",
        itemName: "Ciprofloxacin 500mg",
        category: "Antibiotics",
        sku: "CIP-060",
        availability: "In Stock",
        quantity: 190,
        reorderLevel: 50
    },
    {
        id: "28",
        itemName: "Amoxicillin 875mg",
        category: "Antibiotics",
        sku: "AMO-063",
        availability: "Low Stock",
        quantity: 95,
        reorderLevel: 120
    },
    {
        id: "29",
        itemName: "Azithromycin 250mg",
        category: "Antibiotics",
        sku: "AZI-066",
        availability: "In Stock",
        quantity: 165,
        reorderLevel: 45
    },
    {
        id: "30",
        itemName: "Insulin Glargine",
        category: "DiabetesCare",
        sku: "INS-069",
        availability: "Critical",
        quantity: 30,
        reorderLevel: 75
    },
    {
        id: "31",
        itemName: "Rosuvastatin 20mg",
        category: "CholesterolCo",
        sku: "ROS-072",
        availability: "In Stock",
        quantity: 210,
        reorderLevel: 40
    },
    {
        id: "32",
        itemName: "Esomeprazole 40mg",
        category: "DigestiveHealth",
        sku: "ESO-075",
        availability: "In Stock",
        quantity: 155,
        reorderLevel: 35
    },
    {
        id: "33",
        itemName: "Naproxen 500mg",
        category: "Pain Relief",
        sku: "NAP-078",
        availability: "In Stock",
        quantity: 170,
        reorderLevel: 40
    },
    {
        id: "34",
        itemName: "Tamsulosin 0.4mg",
        category: "UrologyCare",
        sku: "TAM-081",
        availability: "Low Stock",
        quantity: 65,
        reorderLevel: 80
    },
    {
        id: "35",
        itemName: "Duloxetine 60mg",
        category: "MentalWellness",
        sku: "DUL-084",
        availability: "In Stock",
        quantity: 125,
        reorderLevel: 30
    },
    {
        id: "36",
        itemName: "Venlafaxine 75mg",
        category: "MentalWellness",
        sku: "VEN-087",
        availability: "In Stock",
        quantity: 145,
        reorderLevel: 35
    },
    {
        id: "37",
        itemName: "Clonazepam 1mg",
        category: "MentalWellness",
        sku: "CLN-090",
        availability: "Critical",
        quantity: 40,
        reorderLevel: 70
    },
    {
        id: "38",
        itemName: "Diazepam 10mg",
        category: "MentalWellness",
        sku: "DIA-093",
        availability: "In Stock",
        quantity: 100,
        reorderLevel: 25
    },
    {
        id: "39",
        itemName: "Cyclobenzaprine 10mg",
        category: "MuscleCare",
        sku: "CYC-096",
        availability: "In Stock",
        quantity: 135,
        reorderLevel: 30
    },
    {
        id: "40",
        itemName: "Baclofen 20mg",
        category: "MuscleCare",
        sku: "BAC-099",
        availability: "Low Stock",
        quantity: 75,
        reorderLevel: 90
    },
    {
        id: "41",
        itemName: "Methylprednisolone 4mg",
        category: "Inflammation",
        sku: "MET-102",
        availability: "In Stock",
        quantity: 160,
        reorderLevel: 40
    },
    {
        id: "42",
        itemName: "Hydroxyzine 25mg",
        category: "MentalWellness",
        sku: "HYX-105",
        availability: "In Stock",
        quantity: 185,
        reorderLevel: 45
    },
    {
        id: "43",
        itemName: "Buspirone 15mg",
        category: "MentalWellness",
        sku: "BUS-108",
        availability: "In Stock",
        quantity: 120,
        reorderLevel: 30
    },
    {
        id: "44",
        itemName: "Amitriptyline 50mg",
        category: "NerveCare",
        sku: "AMI-111",
        availability: "Low Stock",
        quantity: 80,
        reorderLevel: 100
    },
    {
        id: "45",
        itemName: "Tizanidine 4mg",
        category: "MuscleCare",
        sku: "TIZ-114",
        availability: "In Stock",
        quantity: 140,
        reorderLevel: 35
    },
    {
        id: "46",
        itemName: "Cetirizine 10mg",
        category: "Allergy",
        sku: "CET-117",
        availability: "In Stock",
        quantity: 300,
        reorderLevel: 60
    },
    {
        id: "47",
        itemName: "Loratadine 10mg",
        category: "Allergy",
        sku: "LOR-120",
        availability: "In Stock",
        quantity: 275,
        reorderLevel: 55
    },
    {
        id: "48",
        itemName: "Fexofenadine 180mg",
        category: "Allergy",
        sku: "FEX-123",
        availability: "Critical",
        quantity: 35,
        reorderLevel: 80
    },
    {
        id: "49",
        itemName: "Fluticasone Nasal Spray",
        category: "Allergy",
        sku: "FLU-126",
        availability: "In Stock",
        quantity: 150,
        reorderLevel: 40
    },
    {
        id: "50",
        itemName: "Mometasone Inhaler",
        category: "Respiratory",
        sku: "MOM-129",
        availability: "Low Stock",
        quantity: 55,
        reorderLevel: 75
    }
];

// Detailed inventory data with activities
export const INVENTORY_DETAILS: Record<string, InventoryItemDetail> = {
    "550e8400-e29b-41d4-a716-446655440001": {
        ...RECENT_MOVEMENT_ITEMS[0],
        manufacturer: "Pfizer Inc",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-08-12345",
        scientificName: "Ibuprofen",
        activeIngredient: "Ibuprofen 200mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "NSAID",
        recentActivity: [
            {
                date: "2025-12-19",
                type: "Stock Received",
                amount: 500,
                description: "Stock Requisitioned - 1 day ago"
            },
            {
                date: "2025-12-18",
                type: "Transfer Completed",
                amount: -50,
                description: "+50 units"
            },
            {
                date: "2025-12-17",
                type: "Stock Return",
                amount: 25,
                description: "+25 units"
            },
            {
                date: "2025-04-15",
                type: "Created Order",
                amount: 0,
                description: "Inventory Update"
            },
            {
                date: "2025-04-10",
                type: "Stock Received",
                amount: 500,
                description: "+500 units"
            },
            {
                date: "2025-04-08",
                type: "Stock Requisitioned",
                amount: -100,
                description: "-100 units"
            },
            {
                date: "2025-04-05",
                type: "Transfer Completed",
                amount: -50,
                description: "-50 units"
            }
        ],
        clinicalNotes: "Patient reported symptoms for 3 days. Started with sore throat, now has fever."
    },
    "550e8400-e29b-41d4-a716-446655440002": {
        ...RECENT_MOVEMENT_ITEMS[1],
        manufacturer: "Bristol Myers",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-09-23456",
        scientificName: "Metformin Hydrochloride",
        activeIngredient: "Metformin 500mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "Biguanide",
        recentActivity: [
            {
                date: "2025-12-18",
                type: "Stock Requisitioned",
                amount: -75,
                description: "Emergency requisition - ICU"
            },
            {
                date: "2025-12-15",
                type: "Stock Received",
                amount: 200,
                description: "+200 units"
            },
            {
                date: "2025-12-10",
                type: "Transfer Completed",
                amount: -50,
                description: "Transfer to Pharmacy B"
            }
        ],
        clinicalNotes: "Critical stock level. Urgent reorder required for diabetic patients."
    },
    "550e8400-e29b-41d4-a716-446655440003": {
        ...RECENT_MOVEMENT_ITEMS[2],
        manufacturer: "AstraZeneca",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-07-34567",
        scientificName: "Lisinopril",
        activeIngredient: "Lisinopril 10mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "ACE Inhibitor",
        recentActivity: [
            {
                date: "2025-12-19",
                type: "Stock Received",
                amount: 300,
                description: "Regular stock replenishment"
            },
            {
                date: "2025-12-16",
                type: "Stock Requisitioned",
                amount: -80,
                description: "Cardiology department order"
            },
            {
                date: "2025-12-12",
                type: "Transfer Completed",
                amount: -45,
                description: "Inter-department transfer"
            }
        ],
        clinicalNotes: "Widely prescribed for hypertension management. Maintain adequate stock."
    },
    "550e8400-e29b-41d4-a716-446655440004": {
        ...RECENT_MOVEMENT_ITEMS[3],
        manufacturer: "Merck & Co",
        storageCondition: "Room Temperature (20-25°C)",
        registrationNumber: "REG-2023-06-45678",
        scientificName: "Simvastatin",
        activeIngredient: "Simvastatin 20mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "Statin",
        recentActivity: [
            {
                date: "2025-12-17",
                type: "Stock Received",
                amount: 250,
                description: "Quarterly stock delivery"
            },
            {
                date: "2025-12-14",
                type: "Stock Requisitioned",
                amount: -35,
                description: "Outpatient prescriptions"
            },
            {
                date: "2025-12-09",
                type: "Stock Return",
                amount: 15,
                description: "Returned from satellite clinic"
            }
        ],
        clinicalNotes: "Used for cholesterol management. Low reorder point maintained."
    },
    "550e8400-e29b-41d4-a716-446655440005": {
        ...RECENT_MOVEMENT_ITEMS[4],
        manufacturer: "Abbott Labs",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-05-56789",
        scientificName: "Levothyroxine Sodium",
        activeIngredient: "Levothyroxine 50mcg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "Thyroid Hormone",
        recentActivity: [
            {
                date: "2025-12-18",
                type: "Stock Requisitioned",
                amount: -60,
                description: "Monthly prescriptions filled"
            },
            {
                date: "2025-12-13",
                type: "Stock Received",
                amount: 150,
                description: "Regular delivery"
            },
            {
                date: "2025-12-08",
                type: "Transfer Completed",
                amount: -30,
                description: "Transfer to endocrinology"
            }
        ],
        clinicalNotes: "Essential for thyroid patients. Monitor stock levels closely."
    },
    "550e8400-e29b-41d4-a716-446655440006": {
        ...RECENT_MOVEMENT_ITEMS[5],
        manufacturer: "Novartis",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-04-67890",
        scientificName: "Amlodipine Besylate",
        activeIngredient: "Amlodipine 5mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "Calcium Channel Blocker",
        recentActivity: [
            {
                date: "2025-12-19",
                type: "Stock Received",
                amount: 400,
                description: "Bulk order received"
            },
            {
                date: "2025-12-15",
                type: "Stock Requisitioned",
                amount: -90,
                description: "High demand - hypertension cases"
            },
            {
                date: "2025-12-11",
                type: "Transfer Completed",
                amount: -55,
                description: "Satellite pharmacy transfer"
            }
        ],
        clinicalNotes: "High prescription volume for blood pressure control."
    },
    "550e8400-e29b-41d4-a716-446655440007": {
        ...RECENT_MOVEMENT_ITEMS[6],
        manufacturer: "GlaxoSmithKline",
        storageCondition: "Room Temperature (15-30°C)",
        registrationNumber: "REG-2023-03-78901",
        scientificName: "Omeprazole",
        activeIngredient: "Omeprazole 20mg",
        dosageForm: "Capsule",
        administration: "Oral",
        pharmaClass: "Proton Pump Inhibitor",
        recentActivity: [
            {
                date: "2025-12-16",
                type: "Stock Received",
                amount: 1000,
                description: "Large shipment - high demand item"
            },
            {
                date: "2025-12-12",
                type: "Stock Requisitioned",
                amount: -120,
                description: "Multiple department orders"
            },
            {
                date: "2025-12-07",
                type: "Transfer Completed",
                amount: -80,
                description: "Distribution to clinics"
            }
        ],
        clinicalNotes: "Very high usage medication. No reorder point set due to consistent high stock."
    },
    "550e8400-e29b-41d4-a716-446655440008": {
        ...RECENT_MOVEMENT_ITEMS[7],
        manufacturer: "Sanofi",
        storageCondition: "Room Temperature (15-25°C)",
        registrationNumber: "REG-2023-02-89012",
        scientificName: "Hydrochlorothiazide",
        activeIngredient: "Hydrochlorothiazide 25mg",
        dosageForm: "Tablet",
        administration: "Oral",
        pharmaClass: "Thiazide Diuretic",
        recentActivity: [
            {
                date: "2025-12-10",
                type: "Stock Requisitioned",
                amount: -85,
                description: "Last units dispensed"
            },
            {
                date: "2025-12-05",
                type: "Stock Requisitioned",
                amount: -115,
                description: "High demand period"
            },
            {
                date: "2025-11-28",
                type: "Stock Received",
                amount: 200,
                description: "Previous delivery"
            }
        ],
        clinicalNotes: "OUT OF STOCK - Emergency reorder placed. Expected delivery in 2-3 days."
    }
};

// Helper function to get item order for navigation
export const INVENTORY_ORDER = RECENT_MOVEMENT_ITEMS.map(item => item.id);
